import { CancelButton } from "@/components/admin/cancel-button";
import { DeleteButton } from "@/components/admin/delete-button";
import { SaveButton } from "@/components/admin/form";
import { ShowButton } from "@/components/admin/show-button";
import { cn } from "@/lib/utils";
import type { FormProps } from "ra-core";
import { Form, useRecordContext } from "ra-core";
import type { ReactNode } from "react";
import * as React from "react";
import { Children } from "react";
import { createPortal } from "react-dom";

/**
 * A simple form layout with vertical stacking, validation, and default toolbar.
 *
 * Automatically includes a toolbar with Cancel and Save buttons unless you provide a custom toolbar.
 *
 * @see {@link https://marmelab.com/shadcn-admin-kit/docs/simpleform/ SimpleForm documentation}
 *
 * @example
 * import { Create, SimpleForm, TextInput } from '@/components/admin';
 *
 * const PostCreate = () => (
 *   <Create>
 *     <SimpleForm>
 *       <TextInput source="title" />
 *       <TextInput source="body" />
 *     </SimpleForm>
 *   </Create>
 * );
 */
export const SimpleForm = ({
  children,
  className,
  toolbar = defaultFormToolbar,
  ...rest
}: SimpleFormProps) => (
  <Form
    className={cn(`flex flex-col gap-4 w-full`, className)}
    {...rest}
  >
    {toolbar}
    {children}
  </Form>
);

/**
 * A sticky form toolbar with default Cancel and Save buttons.
 *
 * Provides a consistent action bar for forms that sticks to the bottom of the viewport. By default,
 * renders Cancel and Save buttons, but you can provide custom buttons as children.
 *
 * @example
 * import { FormToolbar, CancelButton, SaveButton } from '@/components/admin';
 *
 * const CustomToolbar = () => (
 *     <FormToolbar>
 *         <CancelButton />
 *         <SaveButton label="Publish" />
 *     </FormToolbar>
 * );
 */
export const FormToolbar = ({
  children,
  className,
  ...rest
}: FormToolbarProps) => {
  const record = useRecordContext();
  const isEdit = !!(record && record.id);
  const [portalNode, setPortalNode] = React.useState<HTMLElement | null>(null);

  React.useEffect(() => {
    // Only target the portal if it exists (in EditView/CreateView)
    setPortalNode(document.getElementById("ra-form-actions-portal"));
  }, []);

  const content = (
    <div
      {...rest}
      className={cn(
        portalNode
          ? "flex flex-row gap-2 items-center"
          : "sticky top-0 z-50 p-4 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 shadow-sm rounded-t-md mb-2 flex items-center justify-end",
        className,
      )}
      role="toolbar"
    >
      {Children.count(children) === 0 ? (
        <div className="flex flex-row gap-2 justify-end items-center">
          {isEdit && <ShowButton />}
          {isEdit && <DeleteButton />}
          <CancelButton />
          <SaveButton />
        </div>
      ) : (
        children
      )}
    </div>
  );

  if (portalNode) {
    return createPortal(content, portalNode);
  }

  return content;
};

export type SimpleFormProps = {
  children: ReactNode;
  className?: string;
  toolbar?: ReactNode;
} & FormProps;

export interface FormToolbarProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: ReactNode;
  className?: string;
}

const defaultFormToolbar = <FormToolbar />;
